import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useResetUserPassword } from '@/hooks/use-users';
import { UserListItem } from '@/types/user';
import { Loader2 } from 'lucide-react';

interface ResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  user: UserListItem | null;
}

export default function ResetPasswordDialog({ open, onClose, user }: ResetPasswordDialogProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const resetMutation = useResetUserPassword(user?.id || '');

  const handleConfirm = async () => {
    if (!user || newPassword !== confirm) return;
    await resetMutation.mutateAsync({ newPassword });
    setNewPassword('');
    setConfirm('');
    onClose();
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setNewPassword('');
      setConfirm('');
      onClose();
    }
  };

  const isValid = newPassword.length >= 8 && newPassword === confirm;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset Password</AlertDialogTitle>
          <AlertDialogDescription>
            Set a new password for <strong>{user?.username}</strong>. Their current session
            will be revoked and they will need to log in again.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min. 8 chars, uppercase, lowercase, number"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter password"
            />
            {confirm && newPassword !== confirm && (
              <p className="text-xs text-destructive">Passwords do not match</p>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={resetMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!isValid || resetMutation.isPending}
          >
            {resetMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Reset Password
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
